import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';

export default function Broadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    api.get('/broadcasts')
      .then(r => { setBroadcasts(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function handleSend(b) {
    if (!window.confirm(`Send "${b.name}" to ${b.channel === 'whatsapp' ? 'WhatsApp' : 'Email'} contacts?`)) return;
    try {
      const r = await api.post(`/broadcasts/${b._id}/send`);
      const costNote = r.data.estimated_cost > 0 ? ` (est. ${fmtMoney(r.data.estimated_cost)})` : '';
      toast.success(`Sending to ${r.data.total} contacts…${costNote}`);
      setBroadcasts(bs => bs.map(x => x._id === b._id ? { ...x, status: 'sending' } : x));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Send failed');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this broadcast?')) return;
    await api.delete(`/broadcasts/${id}`);
    setBroadcasts(bs => bs.filter(b => b._id !== id));
    toast.success('Deleted');
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Broadcasts</h1>
        <Link to="/broadcasts/new" className="btn btn-primary">+ New Broadcast</Link>
      </div>

      {loading
        ? <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading…</div>
        : broadcasts.length === 0
          ? (
            <div className="empty-state">
              <div className="empty-state-icon">📢</div>
              <div className="empty-state-text">No broadcasts yet</div>
              <Link to="/broadcasts/new" className="btn btn-primary btn-sm" style={{ marginTop: 16 }}>
                Create your first broadcast
              </Link>
            </div>
          )
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {broadcasts.map(b => (
                <BroadcastCard key={b._id} b={b} onSend={handleSend} onDelete={handleDelete} />
              ))}
            </div>
          )
      }
    </div>
  );
}

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = [
  { key: 'on_hold',   label: 'On hold',   color: 'var(--warning)',         icon: '⏸' },
  { key: 'sent',      label: 'Sent',      color: 'var(--primary-light)', icon: '📤' },
  { key: 'delivered', label: 'Delivered', color: 'var(--success)',        icon: '✅' },
  { key: 'read',      label: 'Read',      color: 'var(--warning)',        icon: '👁️'  },
  { key: 'clicked',   label: 'Clicked',   color: '#22d3ee',               icon: '🔗' },
  { key: 'failed',    label: 'Failed',    color: 'var(--danger)',         icon: '❌' },
];

function BroadcastCard({ b, onSend, onDelete }) {
  const [activeTab, setActiveTab]     = useState(null); // which status panel is open
  const [panelData, setPanelData]     = useState({});   // { [status]: { recipients, error_summary } }
  const [loadingTab, setLoadingTab]   = useState(null);

  const statusClass = {
    draft: 'badge-gray', scheduled: 'badge-yellow', queued: 'badge-yellow',
    sending: 'badge-yellow', on_hold: 'badge-yellow', sent: 'badge-green', failed: 'badge-red',
  };
  const s = b.stats || {};

  const statValues = {
    on_hold:   s.on_hold   || 0,
    sent:      s.sent      || 0,
    delivered: s.delivered || 0,
    read:      s.read      || 0,
    clicked:   s.clicked   || 0,
    failed:    s.failed    || 0,
  };

  async function handleTabClick(status) {
    if (statValues[status] === 0) return; // nothing to show
    if (activeTab === status) { setActiveTab(null); return; } // toggle off

    setActiveTab(status);
    if (panelData[status]) return; // already loaded

    setLoadingTab(status);
    try {
      const r = await api.get(`/broadcasts/${b._id}/recipients`, { params: { status } });
      setPanelData(d => ({ ...d, [status]: r.data }));
    } catch {
      toast.error('Could not load recipient details');
      setActiveTab(null);
    }
    setLoadingTab(null);
  }

  return (
    <div className="card" style={{ padding: '16px 20px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>{b.channel === 'whatsapp' ? '📱' : '📧'}</span>
            <Link to={`/broadcasts/${b._id}`} style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', textDecoration: 'none' }}
              title="Open broadcast details">
              {b.name}
            </Link>
            <span className={`badge ${statusClass[b.status]}`}>{b.status}</span>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            {b.channel === 'whatsapp' ? `Template: ${b.template_name || '—'}` : `Subject: ${b.subject || '—'}`}
            {b.tags?.length > 0 && <> · {b.tags.map(t => t.name).join(', ')}</>}
            {b.all_contacts && <> · All contacts</>}
            {b.status === 'scheduled' && b.scheduled_at && <> · ⏰ Scheduled for {new Date(b.scheduled_at).toLocaleString()}</>}
            {b.sent_at && <> · {new Date(b.sent_at).toLocaleString()}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {b.status === 'draft'  && <button className="btn btn-success btn-sm" onClick={() => onSend(b)}>▶ Send</button>}
          {b.status === 'failed' && <button className="btn btn-primary btn-sm" onClick={() => onSend(b)}>↺ Retry</button>}
          <Link to={`/broadcasts/${b._id}`} className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>📊 Details</Link>
          {b.status !== 'draft' && b.total_count > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => exportCsv(b)}>⬇ CSV</button>
          )}
          <button className="btn btn-danger btn-sm" onClick={() => onDelete(b._id)}>Delete</button>
        </div>
      </div>

      {/* ── Fatal error ── */}
      {b.send_error && (
        <div style={{ marginTop: 10, padding: '9px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 13 }}>
          <strong style={{ color: 'var(--danger)' }}>❌ Error:</strong>
          <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{b.send_error}</span>
        </div>
      )}

      {/* ── Metrics row ── */}
      {b.status !== 'draft' && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>

            {/* Total (not clickable) */}
            <div style={{ padding: '8px 14px', background: 'var(--surface2)', borderRadius: 8, textAlign: 'center', minWidth: 72 }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{b.total_count}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Total</div>
            </div>

            {/* Cost (not clickable) — sent count × avg rate once sending started */}
            {b.pricing?.estimated_total > 0 && (() => {
              const sentSoFar = s.sent || 0;
              const showActual = sentSoFar > 0 && ['sending', 'sent'].includes(b.status);
              const value = showActual ? sentSoFar * b.pricing.avg_rate : b.pricing.estimated_total;
              return (
                <div title={showActual
                    ? `${sentSoFar} sent × ~$${b.pricing.avg_rate.toFixed(4)}/message (avg for this audience)`
                    : 'Pre-send estimate for the full audience'}
                  style={{ padding: '8px 14px', background: 'var(--surface2)', borderRadius: 8, textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{fmtMoney(value)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    💰 {showActual ? 'Cost (sent)' : 'Est. cost'}
                  </div>
                </div>
              );
            })()}

            {/* Clickable status metrics */}
            {STATUS_CONFIG.map(({ key, label, color, icon }) => {
              const count   = statValues[key];
              const isActive = activeTab === key;
              const loading  = loadingTab === key;
              return (
                <button key={key}
                  onClick={() => handleTabClick(key)}
                  disabled={count === 0}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: `2px solid ${isActive ? color : 'transparent'}`,
                    background: isActive ? `${color}20` : 'var(--surface2)',
                    cursor: count === 0 ? 'default' : 'pointer',
                    opacity: count === 0 ? 0.4 : 1,
                    textAlign: 'center', minWidth: 80,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 18, color }}>{loading ? '…' : count}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {icon} {label} {count > 0 && <span style={{ color }}>{isActive ? '▲' : '▼'}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Progress bar */}
          {b.total_count > 0 && (
            <div className="progress">
              <div className="progress-fill" style={{ width: `${Math.round(((s.sent || 0) / b.total_count) * 100)}%`, background: 'var(--primary)' }} />
            </div>
          )}
        </div>
      )}

      {/* ── Recipient detail panel ── */}
      {activeTab && panelData[activeTab] && (
        <RecipientPanel
          status={activeTab}
          data={panelData[activeTab]}
          config={STATUS_CONFIG.find(c => c.key === activeTab)}
        />
      )}
    </div>
  );
}

function RecipientPanel({ status, data, config }) {
  const { recipients, error_summary, total } = data;
  const { label, color, icon } = config;

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color }}>
        {icon} {label} — {total} contact{total !== 1 ? 's' : ''}
      </div>

      {/* Error summary for failed */}
      {status === 'failed' && error_summary?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Error summary
          </div>
          {error_summary.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, marginBottom: 6, gap: 12 }}>
              <div style={{ fontSize: 13, flex: 1 }}>{e.message}</div>
              <span className="badge badge-red">{e.count}×</span>
            </div>
          ))}
        </div>
      )}

      {/* Recipient table */}
      {recipients.length === 0
        ? <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>No recipients found for this status.</div>
        : (
          <div style={{ maxHeight: 300, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)' }}>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Phone / Email</th>
                  {status === 'sent'      && <th style={thStyle}>Sent at</th>}
                  {status === 'delivered' && <th style={thStyle}>Delivered at</th>}
                  {status === 'read'      && <th style={thStyle}>Read at</th>}
                  {status === 'failed'    && <th style={thStyle}>Reason</th>}
                </tr>
              </thead>
              <tbody>
                {recipients.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
                    <td style={tdStyle}>{r.contact?.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                      {r.contact?.phone || r.contact?.email || '—'}
                    </td>
                    {status === 'sent'      && <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{fmt(r.sent_at)}</td>}
                    {status === 'delivered' && <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{fmt(r.delivered_at)}</td>}
                    {status === 'read'      && <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{fmt(r.read_at)}</td>}
                    {status === 'failed'    && <td style={{ ...tdStyle, color: 'var(--danger)', fontSize: 12 }}>{r.error}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

// Download the per-recipient delivery report (auth header travels with axios,
// so fetch as a blob and trigger the download client-side)
async function exportCsv(b) {
  try {
    const r = await api.get(`/broadcasts/${b._id}/export`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(b.name || 'broadcast').replace(/[^\w-]+/g, '_')}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  } catch {
    toast.error('Export failed');
  }
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase',
};
const tdStyle = { padding: '8px 12px' };

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const fmtMoney = v => `$${v >= 0.01 || v === 0 ? v.toFixed(2) : v.toFixed(4)}`;
